import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { Header } from '../Header';
import { Button } from '../Button';
import { QuizProgressBar } from '../QuizProgressBar';
import { QuestionAnswerSlide } from '../QuestionAnswerSlide';
import { QuestionMultiAnswerSlide } from '../QuestionMultiAnswerSlide';
import { StartSlide } from '../StartSlide';
import { PauseSlide } from '../PauseSlide';
import { ProductSlide } from '../ProductSlide';
import { PlanGraphSlide } from '../PlanGraphSlide';
import { PlanLoaderSlide } from '../PlanLoaderSlide';
import { EmailSlide } from '../EmailSlide';
import { SaleV3 } from '../SaleV3';
import { Timer } from '../Timer';
import { Footer } from '../Footer';
import { AgreementText } from '../AgreementText';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEvents } from '../../shared/use-slide-events';
import { isExpiredPromo } from '../../shared/promo-helpers';
import { getAnswersFromSource } from '../../shared/chapter-answer-helpers';
import {
    EMAIL_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PRODUCT_SLIDE,
    PLAN_LOADER_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v48.png';
import pauseCollage1Src from '../../assets/images/slide/pause-collage-1-self.png';
import pauseCollage2Src from '../../assets/images/slide/pause-collage-2-self.png';
import pauseCollage3Src from '../../assets/images/slide/pause-collage-3-self.png';
import styles from './CommonPrelanderV3.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v48.css';

const cx = classnames.bind(styles);

const IMAGES = [emojiSpriteSrc, pauseCollage1Src, pauseCollage2Src, pauseCollage3Src];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlide,
    [QUESTION_MULTI_ANSWER_SLIDE_COMPONENT]: QuestionMultiAnswerSlide,
    [START_SLIDE]: StartSlide,
    [PAUSE_SLIDE]: PauseSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlide,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlide,
    [EMAIL_SLIDE]: EmailSlide
};

const DELAY = 350;

function CommonPrelanderV3({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlide,
        currentSlideIndex,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);
    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = [QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT].includes(currentSlide.component);
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const numberOfAnsweredSlides = results.userResponses.length + (isQuestionSlide ? 1 : 0);
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));
    const { legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    // (temporary demo)
    useEffect(() => {
        if (isEmailSlide) {
            nextSlide();
        }
    }, [isEmailSlide, nextSlide]);

    const { logQuizLifecycle } = useSlideEvents({ currentSlideIndex: numberOfAnsweredSlides, results, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);
        submitAnswer(answer);
    };

    const setExpiryTimestampDiscount = () => {
        setExpiryTimestampPromo();
        setIsExpired(false);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampDiscount();
        }

        nextSlide(temporaryAnswer);
    };

    const renderLeftNavigation = () => {
        if (finished) {
            // (temporary demo)
            // if (!results.email || selectedSubscriptionData.plan) {
            if (selectedSubscriptionData.plan) {
                return null;
            }

            return (
                <>
                    <Timer expiryTimestamp={isExpired ? null : results.expiryTimestampPromo} label='Discount is reserved for:' />
                    <Button
                        label='Get my plan'
                        type='accent'
                        className={cx('buttonMyPlan', 'animation')}
                        onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                    />
                </>
            );
        }

        return null;
    };

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;
        // (temporary demo)
        // const hasPromoHeader = finished && Boolean(results.email);
        const hasPromoHeader = finished;

        // (temporary demo)
        // isFinished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
        return (
            <Header
                renderLeftNavigation={renderLeftNavigation}
                progressBar={isQuestionSlide && (
                    <QuizProgressBar
                        currentSlideIndex={numberOfAnsweredSlides}
                        totalSlides={quizSlidesIndexing.length}
                    />
                )}
                questionNav={isQuestionSlide && {
                    category: currentSlide.props?.category,
                    currentSlideIndex: numberOfAnsweredSlides,
                    totalSlides: quizSlidesIndexing.length
                }}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', {
                    withBorder: finished,
                    withoutPromo: hasPromoHeader && isExpired,
                    withPromo: hasPromoHeader && !isExpired,
                    withPlan: Boolean(selectedSubscriptionData.plan)
                })}
                isFinished={finished && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            // (temporary demo)
            // if (Boolean(results.email) && !selectedSubscriptionData.plan) {
            if (!selectedSubscriptionData.plan) {
                return <Footer legalEntity={legalEntity} legalLinks={legalLinks} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={cx('slideFooter', { withBorder: currentSlide.component === QUESTION_MULTI_ANSWER_SLIDE_COMPONENT })}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='accent'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            inSlide
                            disabled={currentSlide.component === QUESTION_MULTI_ANSWER_SLIDE_COMPONENT ? (temporaryAnswer.answerIds?.length || 0) < (currentSlide.continueButton?.minAnswers || 1) : false}
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV3
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    onExpirePromo={() => setIsExpired(true)}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                />
            );
        }

        // (temporary demo)
        if (isEmailSlide) {
            return null;
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);
        const dynamicAnswers = getAnswersFromSource(currentSlide, results);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide
                })}
            >
                <Slide
                    {...currentSlide.props}
                    answers={dynamicAnswers || currentSlide.props?.answers}
                    spriteVersion='v48'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                />
                {isStartSlide && <AgreementText legalLinks={legalLinks} />}
            </div>
        );
    };

    // (temporary demo)
    // stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan
    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV3
};
